import { useRef, useEffect } from 'react';

import { Heading, VStack, SimpleGrid } from '@chakra-ui/react';

import MealItemCard from '~/components/MealItemCard';

import { useNavigate, useParams } from 'react-router-dom';

import storeCategoryAtom from '~/recoil/atom/storeCategory';
import { useSetRecoilState } from 'recoil';

import { CategoryWithItems } from '~/pages/Store/hooks/useStoreMenuList';

import * as fp from 'lodash/fp';

import { PLATFORM_ID } from '~/globalVariables';

type StoreMealListProps = {
  selectedCategory: { id: string; name: string };
  categoriesWithItems: Array<CategoryWithItems>;
};

export default function StoreMealList({
  selectedCategory,
  categoriesWithItems,
}: StoreMealListProps) {
  return (
    <VStack align="stretch" spacing={6}>
      {categoriesWithItems.map((categoryWithItems) => (
        <StoreMeal
          key={categoryWithItems.id}
          categoryWithItems={categoryWithItems}
          selectedCategory={selectedCategory}
        />
      ))}
    </VStack>
  );
}

type StoreMealProps = {
  categoryWithItems: CategoryWithItems;
  selectedCategory: { id: string; name: string };
};

function StoreMeal({ categoryWithItems, selectedCategory }: StoreMealProps) {
  const { storeId = '', groupId = '' } = useParams();
  const navigate = useNavigate();
  const categoryRef = useRef<HTMLDivElement>(null);

  const setStoreCategory = useSetRecoilState(storeCategoryAtom);

  const { name, items, id } = categoryWithItems;

  useEffect(() => {
    if (fp.eq(selectedCategory.id, id) && categoryRef.current) {
      const position = categoryRef.current.getBoundingClientRect();
      const htmlEle = document.querySelector('html');
      const bodyPosition = htmlEle?.scrollTop || 0;
      const screenHeight = htmlEle?.clientHeight || 0;

      window.scroll({
        top: bodyPosition + position.top - screenHeight / 2,
        behavior: 'smooth',
      });
    }
  }, [selectedCategory.id, id]);

  return (
    <VStack align="stretch" ref={categoryRef}>
      <Heading fontSize="lg">{name}</Heading>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
        {items.map((item) => (
          <MealItemCard
            key={`${id}_${item.id}`}
            name={item.name}
            description={item.description}
            tags={item.tags[PLATFORM_ID] || []}
            price={item.price}
            image={item.image}
            onNavigate={() => {
              setStoreCategory(id);
              navigate(
                groupId
                  ? `/group/${groupId}/store/${storeId}/item/${item.id}/add`
                  : `/store/${storeId}/item/${item.id}/add`,
                { state: { previous: window.location.pathname } },
              );
            }}
          />
        ))}
      </SimpleGrid>
    </VStack>
  );
}
